import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import Title from "antd/es/typography/Title";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  checkIfFileTypeIsValidForImageAttachment,
  dateObjToISOFormat,
  readImageFileAsync,
  sanitizeDataTypeClientSide,
} from "../../../../../../utils/utils";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import { fileImageUpload } from "../../../../../../redux/services/auth/authServices";
import { FileAttachment } from "../../../../../common/fileAttachment";
import {
  addOffer,
  getOfferById,
  updateOffer,
} from "../../../../../../redux/services/offer/offerServices";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  CameraOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  baseRouteMaker,
  routes,
} from "../../../../../../constants/routeConsts";
import { getCategoriesSelectListAction } from "../../../../../../redux/services/categories/categoriesServices";
import { toastService } from "../../../../../common/toast-messages/toast";
import { allowCharactersOnlyForDecimalNumbers } from "../../../../../../utils/appKeyStrokeLimitingUtils";
import TextArea from "antd/es/input/TextArea";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { LanguageContext } from "../../../../../../context/LanguageProvider";
import { colors } from "../../../../../../constants/colors";
import { InfoButtonIcon } from "../../../../../../assets/svg";
import "../OffersAndPackages.scss"

const OfferForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const category = Form.useWatch("category", { form, preserve: true });
  const discountType = Form.useWatch("discountType", { form, preserve: true });
  const startDate = Form.useWatch("startDate", { form, preserve: true });
  const bulletPoints = Form.useWatch("bulletPoints", { form, preserve: true });
  const bulletPoints_ar = Form.useWatch("bulletPoints_ar", {
    form,
    preserve: true,
  });

  const { selectedLanguage } = useContext(LanguageContext);

  const { selectedOfferId } = useSelector((state) => state?.offers);

  const [attachmentObj, setAttachmentObj] = useState(null);
  const [singleAttachmetSelectedImageUrl, setSingleAttachmetSelectedImageUrl] =
    useState("");

  /* module dependencies for attachment */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  const attachmentRef = useRef(null);
  /* module dependencies for attachment */

  /* open image preview for dialog */
  const handleAttachmentPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await readImageFileAsync(file.originFileObj);
    }
    setPreviewImageSrc(file?.url || file?.preview);
    setOpenPreviewDialog(true);
    const tempPreviewTitle =
      file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
    setPreviewTitle(tempPreviewTitle);
  };
  /* open image preview for dialog */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [imageLoader, setImageLoader] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* select list dependencies */
  const [selectedCategories, setSelectedCategories] = useState();
  const { categoriesSelectList } = useSelector((state) => state.categories);
  /* select list dependencies */

  /* function definition for select list API calls */
  const selectListApiCalls = (details) => {
    // Categories select list
    dispatch(
      getCategoriesSelectListAction(setSelectListLoader, selectedLanguage)
    ).then((apiRes) => {
      const categories = apiRes;

      const tempCategories =
        details &&
        categories &&
        categories
          ?.filter((element) => {
            return details?.categories?.some((_element) => {
              const _elementName =
                selectedLanguage?.value === "ar"
                  ? _element?.locales[0]?.name
                  : _element.name;
              return _elementName === element.name;
            });
          })
          .map((element) => ({
            value: element._id,
            label: element.name,
            _id: element._id,
          }));

      setSelectedCategories(tempCategories);
      form.setFieldValue("categories", tempCategories);
    });
  };
  /* function definition for select list API calls */

  useEffect(() => {
    if (
      selectedOfferId &&
      selectedOfferId !== "" &&
      selectedOfferId !== null &&
      selectedOfferId !== undefined
    ) {
      const offerBody = {
        selectedOfferId,
      };
      dispatch(getOfferById(offerBody, navigate, setLoading)).then((apiRes) => {
        const offer = apiRes;
        selectListApiCalls(offer);
        const startDate = dayjs(offer?.offerStartDate);
        const endDate = dayjs(offer?.offerEndDate);
        form.setFieldsValue({
          offername: offer?.title,
          offername_ar: offer?.locales?.[0].title,
          discountType: offer?.discountType,
          discountValue: offer?.discountValue,
          startDate: startDate || null,
          endDate: endDate || null,
          description: offer?.description,
          description_ar: offer?.locales?.[0].description,
          bulletPoints: offer?.bulletPoints,
          bulletPoints_ar: offer?.locales?.[0]?.bulletPoints,
        });

        setSingleAttachmetSelectedImageUrl(offer?.banner);
        setAttachmentList(
          offer?.banner?.map((element) => ({
            ...element,
            url: element?.url,
          }))
        );
      });
    } else {
      selectListApiCalls();
    }
  }, []);

  /* on Category changed */
  const onCategoryChanged = (id, value) => {
    if (value && value !== null && value !== undefined) {
      setSelectedCategories(value);
    }
  };
  /* on Category changed */

  const onFinish = (values) => {
    if (!attachmentList || attachmentList?.length === 0) {
      toastService.error(t("pleaseSelectAtleastOneImage"));
      return;
    }
    let dataToBeSent = {
      _id: selectedOfferId ?? "",
      title: values?.offername,
      title_ar: values?.offername_ar,
      discountType: values?.discountType,
      discountValue: values?.discountValue,
      offerStartDate: dateObjToISOFormat(values?.startDate),
      offerEndDate: dateObjToISOFormat(values?.endDate),
      description: values?.description,
      description_ar: values?.description_ar,
      banner: attachmentList?.map((element) => ({
        name: element?.name,
        url: element?.url,
      })),
      isSponsored: values?.isSponsored ?? false,
      categories: selectedCategories?.map((element) => element._id),
      bulletPoints: values?.bulletPoints,
      bulletPoints_ar: values?.bulletPoints_ar,
    };
    if (selectedOfferId) {
      const { isSponsored, ...dataToBeSentForEdit } = dataToBeSent;
      dispatch(updateOffer(dataToBeSentForEdit, navigate, setLoading));
    } else {
      dispatch(addOffer(dataToBeSent, navigate, setLoading));
    }
  };

  /* start date and end date validation dependencies */
  const disabledStartDate = (currentDate) => {
    return currentDate && dayjs(currentDate).isBefore(dayjs(), "day");
  };

  const disabledEndDate = (value) => {
    if (!startDate) {
      return true;
    }
    const allowedEndDate = new Date(startDate);
    return value < allowedEndDate.setDate(allowedEndDate.getDate() + 1);
  };
  /* start date and end date validation dependencies */

  return (
    <div className="offerListContainer">
      <Flex vertical gap="2rem">
        <Flex gap="1rem">
          <Button
            type="default"
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(baseRouteMaker(routes.OFFER))}
          />
          <Title level={2}>{`${
            selectedOfferId ? t("activeSubs.edit") : t("CommonUtils.add")
          } ${t("Modules.Ads.offer")}`}</Title>
        </Flex>
        <Row gutter={24} justify="start">
          <Col span={12}>
            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              autoComplete="off"
              requiredMark={false}
              style={{ maxWidth: "500px" }}
            >
              <Form.Item
                label={
                  <RequiredLabel
                    text={t("Modules.Offer.OfferForm.offerNameLabel")}
                    required={true}
                  />
                }
                name="offername"
                rules={[
                  {
                    required: true,
                    message: t("Modules.Offer.OfferForm.offerNameErrorMessage"),
                  },
                  // {
                  //   pattern: /^[A-Za-z\s]+$/,
                  //   message: t(
                  //     "Modules.Offer.OfferForm.offerNameValidErrorMessage"
                  //   ),
                  // },
                  {
                    whitespace: true,
                    message: t("Modules.Offer.OfferForm.offerNameErrorMessage"),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input
                  placeholder={t(
                    "Modules.Offer.OfferForm.offerNamePlaceholder"
                  )}
                />
              </Form.Item>
              <Form.Item
                label={
                  <RequiredLabel
                    text={`${t("Modules.Offer.OfferForm.offerNameLabel")} ${t(
                      "CommonUtils.inArabic"
                    )}`}
                    required={true}
                  />
                }
                name="offername_ar"
                rules={[
                  {
                    required: true,
                    message: `${t(
                      "Modules.Offer.OfferForm.offerNameErrorMessage"
                    )} ${t("CommonUtils.inArabic")}`,
                  },

                  {
                    whitespace: true,
                    message: `${t(
                      "Modules.Offer.OfferForm.offerNameErrorMessage"
                    )} ${t("CommonUtils.inArabic")}`,
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input
                  dir="rtl"
                  placeholder={`${t(
                    "Modules.Offer.OfferForm.offerNamePlaceholder"
                  )} ${t("CommonUtils.inArabic")}`}
                />
              </Form.Item>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="discountType"
                    label={
                      <RequiredLabel
                        text={t("Modules.Offer.OfferForm.discountType")}
                        required={true}
                      />
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          "Modules.Offer.OfferForm.pleaseSelectDiscountType"
                        ),
                      },
                    ]}
                  >
                    <Select
                      options={[
                        { value: "amount", label: "Amount" },
                        { value: "percentage", label: "Percentage" },
                      ]}
                      placeholder={t(
                        "Modules.Offer.OfferForm.selectDiscountType"
                      )}
                      onChange={() => form.resetFields(["discountValue"])}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="discountValue"
                    label={
                      <RequiredLabel
                        text={
                          discountType === "amount"
                            ? t("Modules.Offer.OfferForm.discountValue")
                            : discountType === "percentage"
                            ? t("Modules.Offer.OfferForm.discountPercent")
                            : t("Modules.Offer.OfferForm.discountValue")
                        }
                        required={true}
                      />
                    }
                    rules={[
                      {
                        required: true,
                        message:
                          discountType === "amount"
                            ? t(
                                "Modules.Offer.OfferForm.pleaseEnterDiscountValue"
                              )
                            : discountType === "percentage"
                            ? t(
                                "Modules.Offer.OfferForm.pleaseEnterDiscountPercent"
                              )
                            : t(
                                "Modules.Offer.OfferForm.pleaseEnterDiscountValue"
                              ),
                      },
                      {
                        validator: (_, value) => {
                          if (discountType === "percentage") {
                            if (value && value > 100) {
                              return Promise.reject(
                                `${t(
                                  "Modules.Offer.OfferForm.discountPercent"
                                )} ${t(
                                  "CommonUtils.shouldNotBeGreaterThan100"
                                )}`
                              );
                            } else if (value && value < 1) {
                              return Promise.reject(
                                `${t(
                                  "Modules.Offer.OfferForm.discountPercent"
                                )} ${t("CommonUtils.shouldBeGreaterThan0")}`
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      placeholder={
                        discountType === "amount"
                          ? t("Modules.Offer.OfferForm.enterDiscountValue")
                          : discountType === "percentage"
                          ? t("Modules.Offer.OfferForm.enterDiscountPercent")
                          : t("Modules.Offer.OfferForm.enterDiscountValue")
                      }
                      onKeyDown={(e) => allowCharactersOnlyForDecimalNumbers(e)}
                      disabled={!discountType}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("Modules.Offer.OfferForm.startDateLabel")}
                    required={true}
                  />
                }
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: t("Modules.Offer.OfferForm.startDateErrorMessage"),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%", cursor: "pointer" }}
                  placeholder={t(
                    "Modules.Offer.OfferForm.startDatePlaceholder"
                  )}
                  disabledDate={disabledStartDate}
                  onChange={() => form.setFieldValue("endDate", null)}
                  onKeyDown={(event) => event.preventDefault()}
                  format={"DD-MM-YYYY"}
                />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("Modules.Offer.OfferForm.endDateLabel")}
                    required={true}
                  />
                }
                name="endDate"
                rules={[
                  {
                    required: true,
                    message: t("Modules.Offer.OfferForm.endDateErrorMessage"),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%", cursor: "pointer" }}
                  placeholder={t("Modules.Offer.OfferForm.endDatePlaceholder")}
                  disabledDate={disabledEndDate}
                  onKeyDown={(event) => event.preventDefault()}
                  format={"DD-MM-YYYY"}
                />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("Modules.Offer.OfferForm.descriptionLabel")}
                    required={true}
                  />
                }
                name="description"
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.Offer.OfferForm.descriptionErrorMessage"
                    ),
                  },
                  {
                    whitespace: true,
                  },
                ]}
                // normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <TextArea
                  rows={4}
                  showCount
                  maxLength={
                    appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                  }
                  placeholder={t(
                    "CommonUtils.enterDescriptionUpto255Characters"
                  )}
                />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={`${t("Modules.Offer.OfferForm.descriptionLabel")} ${t(
                      "CommonUtils.inArabic"
                    )}`}
                    required={true}
                  />
                }
                name="description_ar"
                rules={[
                  {
                    required: true,
                    message: `${t(
                      "Modules.Offer.OfferForm.descriptionErrorMessage"
                    )} ${t("CommonUtils.inArabic")}`,
                  },
                  {
                    whitespace: true,
                  },
                ]}
                // normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <TextArea
                  rows={4}
                  showCount
                  maxLength={
                    appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                  }
                  placeholder={`${t(
                    "CommonUtils.enterDescriptionUpto255Characters"
                  )} ${t("CommonUtils.inArabic")}`}
                  dir="rtl"
                />
              </Form.Item>

              <Form.Item
                name="categories"
                label={
                  <RequiredLabel
                    text={t("Modules.Staff.StaffForm.category")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.Staff.StaffForm.pleaseSelectCategory"),
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  loading={selectListLoader}
                  options={categoriesSelectList}
                  placeholder={t("Modules.Staff.StaffForm.selectCategory")}
                  onChange={onCategoryChanged}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.name ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {/* form multiple list - bulletPoints */}
              <Form.List name="bulletPoints">
                {(fields, { add, remove }) => (
                  <>
                    <Form.Item>
                      <Flex justify="space-between">
                        <Title level={3}>{t("keyPoints")}</Title>
                        <Button
                          type="primary"
                          shape="circle"
                          size="small"
                          disabled={bulletPoints?.length >= 5}
                          icon={<PlusOutlined />}
                          onClick={() => add()}
                        />
                      </Flex>
                    </Form.Item>
                    <div
                      style={{
                        display: "flex",
                        rowGap: 16,
                        flexDirection: "column",
                      }}
                    >
                      {fields.map(({ key, name, ...restField }) => (
                        <Card
                          size="small"
                          title={`Key Point ${name + 1}`}
                          key={key}
                          extra={
                            <Button
                              type="default"
                              shape="circle"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          }
                        >
                          <Form.Item {...restField} name={[name]}>
                            <Input />
                          </Form.Item>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </Form.List>
              {/* form multiple list - bulletPoints */}

              {/* form multiple list - bulletPoints in arabic */}
              <Form.List name="bulletPoints_ar">
                {(fields, { add, remove }) => (
                  <>
                    <Form.Item>
                      <Flex justify="space-between">
                        <Title level={3}>{`${t("keyPoints")} ${t(
                          "CommonUtils.inArabic"
                        )}`}</Title>
                        <Button
                          type="primary"
                          shape="circle"
                          size="small"
                          disabled={bulletPoints_ar?.length >= 5}
                          icon={<PlusOutlined />}
                          onClick={() => add()}
                        />
                      </Flex>
                    </Form.Item>
                    <div
                      style={{
                        display: "flex",
                        rowGap: 16,
                        flexDirection: "column",
                      }}
                    >
                      {fields.map(({ key, name, ...restField }) => (
                        <Card
                          size="small"
                          title={`Key Point ${name + 1} ${t(
                            "CommonUtils.inArabic"
                          )}`}
                          key={key}
                          extra={
                            <Button
                              type="default"
                              shape="circle"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          }
                        >
                          <Form.Item {...restField} name={[name]}>
                            <Input dir="rtl" />
                          </Form.Item>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </Form.List>
              {/* form multiple list - bulletPoints_ar */}

              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
                disabled={loading}
              >
                {`${
                  selectedOfferId
                    ? t("Modules.Offer.OfferForm.editOffer")
                    : t("Modules.Offer.OfferForm.addOffer")
                }`}
              </Button>
            </Form>
          </Col>

          <Col span={7}>
            <Typography.Title level={4} style={{ marginBottom: 10 }}>
              <Flex gap="0.2rem" align="center">
                <RequiredLabel text={t("UploadImages")} required={true} />
                <Tooltip
                  placement="topLeft"
                  title={t("CommonUtils.multipleImagesTooltip")}
                  color={colors.DarkMint}
                >
                  <Button type="text" shape="circle" size="small">
                    <Flex align="center" justify="center">
                      <InfoButtonIcon />
                    </Flex>
                  </Button>
                </Tooltip>
              </Flex>
            </Typography.Title>
            <Flex
              style={{ flexDirection: "column-reverse", textAlign: "center" }}
            >
              <Flex vertical gap="0.5rem">
                <div
                  className="attachmentContainer"
                  style={{ cursor: "pointer" }}
                  onClick={() => attachmentRef.current?.click()}
                >
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<PlusOutlined style={{ fontSize: "22px" }} />}
                  />
                </div>
                <Flex
                  vertical
                  style={{ width: "385px" }}
                  align="center"
                  justify="center"
                >
                  <Typography.Title level={3} type="secondary">
                    {t("CommonUtils.supportedImageFormats")}
                  </Typography.Title>

                  <Typography.Title
                    level={3}
                    type="secondary"
                    style={{ margin: 5 }}
                  >
                    {t("CommonUtils.maxImage")}
                  </Typography.Title>

                  <Typography.Title
                    level={3}
                    type="success"
                    style={{ margin: 0, textAlign: "center" }}
                  >
                    {t("CommonUtils.addImagesInParticular")}
                    <br />
                    {t("CommonUtils.dimension1")}
                    <br />
                    {t("CommonUtils.or")}
                    <br />
                    {t("CommonUtils.dimension2")}
                    <br />
                  </Typography.Title>
                </Flex>
              </Flex>
              <Flex vertical align="center" justify="center">
                <Upload
                  listType="picture-card"
                  fileList={attachmentList}
                  multiple
                  accept="image/*"
                  onPreview={handleAttachmentPreview}
                  onChange={(options) => {
                    const tempFileTypeIsValidResArr = options?.fileList?.map(
                      (element) =>
                        checkIfFileTypeIsValidForImageAttachment(element.name)
                    );

                    const isValid = !tempFileTypeIsValidResArr?.some(
                      (element) => element.isValid === false
                    );
                    const fileTypeIsValidRes = tempFileTypeIsValidResArr?.find(
                      (element) => element.isValid === false
                    );

                    if (isValid === false) {
                      toastService.error(fileTypeIsValidRes.isInvalidMessage);
                    } else {
                      setAttachmentList(options.fileList);
                    }
                  }}
                  onRemove={(file) =>
                    setAttachmentList(
                      attachmentList.filter(
                        (element) => element.uid !== file.uid
                      )
                    )
                  }
                  customRequest={(options) => {
                    console.log(options, "options");
                    const attachmentFileName = options.file.name;
                    const fileTypeIsValidRes =
                      checkIfFileTypeIsValidForImageAttachment(
                        attachmentFileName
                      );

                    if (fileTypeIsValidRes.isValid === false) {
                      toastService.error(fileTypeIsValidRes.isInvalidMessage);
                      return;
                    } else {
                      const formData = new FormData();
                      formData.append("image", options.file);
                      dispatch(fileImageUpload(formData)).then((apiRes) => {
                        const imageUrl = apiRes?.[0];
                        options.file["url"] = imageUrl;
                        options.onSuccess(imageUrl, options.file);
                      });
                    }
                  }}
                  className="uploadContainer"
                >
                  <div ref={attachmentRef}></div>
                </Upload>
                <Modal
                  open={openPreviewDialog}
                  title={previewTitle}
                  footer={null}
                  onCancel={() => setOpenPreviewDialog(false)}
                >
                  <img style={{ width: "100%" }} src={previewImageSrc} />
                </Modal>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </Flex>
    </div>
  );
};

export default OfferForm;
